// `@ImageN` ↔ `@Nombre` — ADR-2026-10-04-las-menciones-de-video-se-ven-por-nombre.
//
// Seedance nombra las fotos por posición (`@Image1`, `@Image2`: el orden del array de
// referencias que viaja) y el escritor del Creador escribe así. Para el usuario eso no
// significa nada: en la caja de Redes las referencias tienen nombre (el producto, el
// modelo, "Imagen 2"), que es lo que lista el menú de la `@` y lo que pinta el resaltado.
// Acá se traduce en los dos sentidos, siempre contra la MISMA lista de referencias que
// se mandó o se va a mandar (cada una sabe de qué adjunto salió: `adjuntoId`), así la
// posición y el nombre nunca se desencuentran. Puro, para probarse.

import type { ChatAttachment } from '../../agentAttachments';
import { nombreDeMencion } from '../../mencionesDeAdjuntos';
import { tramosResaltados } from '../../../../chat-atlas/ui/resaltadoDeTexto';

/** Lo mínimo que hace falta de una referencia resuelta: de qué adjunto salió. */
export interface ReferenciaConOrigen {
  adjuntoId?: string;
}

const IMAGEN_N = /@Image(\d+)\b/g;

/**
 * Lo que devolvió el escritor, para la caja: cada `@ImageN` pasa a `@Nombre` del adjunto
 * que ocupaba esa posición en las referencias que se le mandaron. Un `@ImageN` sin
 * adjunto (una posición que no existe, o una referencia que no vino de un adjunto) queda
 * como está.
 */
export function deImagenANombre(
  prompt: string,
  referencias: readonly ReferenciaConOrigen[],
  adjuntos: readonly ChatAttachment[],
): string {
  return prompt.replace(IMAGEN_N, (todo, n: string) => {
    const id = referencias[Number(n) - 1]?.adjuntoId;
    const adjunto = id ? adjuntos.find((a) => a.id === id) : undefined;
    const nombre = adjunto ? nombreDeMencion(adjunto, adjuntos) : '';
    return nombre ? `@${nombre}` : todo;
  });
}

/**
 * Lo que se va a mandar: cada `@Nombre` de un adjunto pasa a `@ImageN`, con N su posición
 * (desde 1) en las referencias que viajan. Si el adjunto mencionado no está entre las
 * referencias (se lo sacó después de escribir, o no entró en el tope), la mención queda
 * como texto plano sin la `@`: nunca un `@Image` que el proveedor no tenga.
 */
export function deNombreAImagen(
  prompt: string,
  referencias: readonly ReferenciaConOrigen[],
  adjuntos: readonly ChatAttachment[],
): string {
  const menciones = tramosResaltados(prompt, adjuntos).filter((t) => t.tipo === 'mencion');
  let out = prompt;
  // De atrás para adelante, así los índices de los tramos anteriores no se mueven.
  for (const m of [...menciones].reverse()) {
    const pos = m.adjuntoId ? referencias.findIndex((r) => r.adjuntoId === m.adjuntoId) : -1;
    const reemplazo = pos >= 0 ? `@Image${pos + 1}` : prompt.slice(m.desde + 1, m.hasta);
    out = `${out.slice(0, m.desde)}${reemplazo}${out.slice(m.hasta)}`;
  }
  return out;
}
